import { useEffect, useState } from "react";

import Input from "../../../components/common/Input";
import Select from "../../../components/common/Select";
import Button from "../../../components/common/Button";
import ErrorMessage from "../../../components/common/ErrorMessage";

import {
  MANAGEABLE_ROLES,
  ROLE_LABELS
} from "../constants/user.constants";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  password: "",
  role: ""
};

const UserForm = ({
  initialData = null,
  onSubmit,
  onCancel,
  loading = false,
  serverError = "",
  mode = "create"
}) => {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(
    EMPTY_FORM
  );

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        password: "",
        role:
          initialData.roles?.[0] || ""
      });
    } else {
      setFormData(EMPTY_FORM);
    }

    setErrors({});
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: ""
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name =
        "Name must contain at least 2 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email
      )
    ) {
      newErrors.email = "Invalid email address";
    }

    if (formData.phone.length > 30) {
      newErrors.phone =
        "Phone number is too long";
    }

    if (!isEdit) {
      if (!formData.password) {
        newErrors.password =
          "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password =
          "Password must contain at least 8 characters";
      } else if (formData.password.length > 72) {
        newErrors.password =
          "Password cannot exceed 72 characters";
      }

      if (!formData.role) {
        newErrors.role = "Role is required";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim() || null
    };

    if (!isEdit) {
      payload.password = formData.password;
      payload.role = formData.role;
    }

    try {
      await onSubmit(payload);
    } catch (error) {
      const details =
        error?.response?.data?.details;

      if (details) {
        setErrors(details);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5"
    >
      {serverError && (
        <ErrorMessage message={serverError} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Input
          label="Full name"
          name="name"
          placeholder="Enter full name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          disabled={loading}
        />

        <Input
          label="Email address"
          name="email"
          type="email"
          placeholder="user@company.com"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          disabled={loading}
        />

        <Input
          label="Phone number"
          name="phone"
          type="tel"
          placeholder="+91 98765 43210"
          value={formData.phone}
          onChange={handleChange}
          error={errors.phone}
          disabled={loading}
        />

        {!isEdit && (
          <Select
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            error={errors.role}
            disabled={loading}
            options={[
              {
                value: "",
                label: "Select role"
              },
              ...MANAGEABLE_ROLES.map(
                (role) => ({
                  value: role,
                  label:
                    ROLE_LABELS[role] || role
                })
              )
            ]}
          />
        )}

        {!isEdit && (
          <div className="md:col-span-2">
            <Input
              label="Password"
              name="password"
              type="password"
              placeholder="Minimum 8 characters"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          loading={loading}
          disabled={loading}
        >
          {isEdit
            ? "Update user"
            : "Create user"}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;